import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from "lucide-react";

let workerConfigured = false;

async function configurePdfWorker() {
  if (workerConfigured) return;
  // Load the matching worker implementation locally, never from a third-party CDN.
  // PDF.js uses this registered handler for its in-process worker.
  const worker = await import('pdfjs-dist/build/pdf.worker.mjs');
  globalThis.pdfjsWorker = worker;
  workerConfigured = true;
}

export default function PdfViewer({ fileUrl, title = "PDF Vorschau", reloadKey = 0, className = "", showOpenButton = true, compactLandscape = false, sideNavigation = true }) {
  const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 639px)').matches ?? false);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 639px)');
    if (!media) return undefined;
    const update = () => { setMobile(media.matches); setZoom(1); };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const docRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [pdfjsLib, setPdfjsLib] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [innerReload, setInnerReload] = useState(0);
  const [useNativeFallback, setUseNativeFallback] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const lib = await import("pdfjs-dist/build/pdf.mjs");
        if (!active) return;
        await configurePdfWorker();
        if (!active) return;
        setPdfjsLib(lib);
      } catch {
        if (!active) return;
        setError("PDF-Viewer konnte nicht initialisiert werden.");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPage(1);
    setZoom(1);
    setUseNativeFallback(false);
  }, [fileUrl, reloadKey, innerReload]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setViewportSize({
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
    };

    updateSize();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSize);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const renderPage = useCallback(
    async (targetPage) => {
      if (!pdfjsLib || !docRef.current || !canvasRef.current) return;
      if (renderTaskRef.current?.cancel) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
      }
      const pdfPage = await docRef.current.getPage(targetPage);
      const canvas = canvasRef.current;
      const naturalViewport = pdfPage.getViewport({ scale: 1 });
      const measuredWidth = viewportSize.width || viewportRef.current?.clientWidth || window.innerWidth - 96;
      const widthBudget = Math.max(1, Math.min(760, measuredWidth - (mobile ? 16 : sideNavigation ? 112 : 32)));
      const fitScale = widthBudget / naturalViewport.width;
      const scale = Number.isFinite(fitScale) && fitScale > 0
        ? Math.min(1.25, fitScale) * (mobile ? zoom : 1)
        : 0.72;
      const viewport = pdfPage.getViewport({ scale });
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, viewport.width, viewport.height);
      renderTaskRef.current = pdfPage.render({
        canvasContext: ctx,
        viewport,
      });
      await renderTaskRef.current.promise;
    },
    [pdfjsLib, viewportSize.width, sideNavigation, mobile, zoom]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!pdfjsLib || !fileUrl) return;
      setLoading(true);
      setError("");
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: fileUrl,

        });
        const doc = await loadingTask.promise;
        if (!active) return;
        docRef.current = doc;
        setPageCount(doc.numPages || 1);
        setUseNativeFallback(false);
      } catch {
        if (!active) return;
        // Graceful fallback to native browser PDF rendering
        setUseNativeFallback(true);
        setError("");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
      try {
        docRef.current.destroy?.();
      } catch {
        // ignore
      }
      docRef.current = null;
    };
  }, [pdfjsLib, fileUrl, reloadKey, innerReload]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!docRef.current || !page) return;
      setLoading(true);
      try {
        await renderPage(page);
      } catch (renderError) {
        // Rapid zoom changes cancel the previous render; this is not a PDF failure.
        if (active && renderError?.name !== 'RenderingCancelledException') {
          setUseNativeFallback(true);
          setError("");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [page, pageCount, renderPage, viewportSize.height, viewportSize.width]);

  return (
    <div className={`flex min-h-0 flex-col gap-3 ${compactLandscape ? "h-auto" : "h-full"} ${className}`}>
      <div className={`shrink-0 flex flex-wrap items-center gap-2 px-1 sm:px-4 ${title ? "justify-between" : "justify-end"}`}>
        {title ? <p className="text-sm font-medium text-[#1D1D1F]">{title}</p> : null}
        <div className="flex items-center gap-2">
          {fileUrl && showOpenButton && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="h-8 gap-2"
              title="PDF herunterladen / öffnen"
            >
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" aria-label="PDF herunterladen oder öffnen"><Download className="h-4 w-4" />Download</a>
            </Button>
          )}
          {!useNativeFallback && (
            <>
              {!sideNavigation && <Button
                type="button"
                variant="outline"
                size="sm"
                className="btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Zurück
              </Button>}
              <span aria-live="polite" className="text-xs text-[#86868B]">{page} / {pageCount || 1}</span>
              {!sideNavigation && <Button
                type="button"
                variant="outline"
                size="sm"
                className="btn-secondary"
                disabled={!pageCount || page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount || p, p + 1))}
              >
                Weiter
              </Button>}
            </>
          )}
        </div>
      </div>

      {mobile && !useNativeFallback && !error && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2" aria-label="PDF-Navigation und Zoom">
          {sideNavigation && <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="icon" aria-label="Vorherige PDF-Seite" disabled={loading || page <= 1} onClick={() => { setPage(p => Math.max(1, p - 1)); viewportRef.current?.scrollTo?.(0, 0); }}><ChevronLeft className="h-5 w-5" /></Button>
            <Button type="button" variant="outline" size="icon" aria-label="Nächste PDF-Seite" disabled={loading || !pageCount || page >= pageCount} onClick={() => { setPage(p => Math.min(pageCount, p + 1)); viewportRef.current?.scrollTo?.(0, 0); }}><ChevronRight className="h-5 w-5" /></Button>
          </div>}
          <div className="ml-auto flex items-center gap-1">
            <Button type="button" variant="outline" size="icon" aria-label="PDF verkleinern" disabled={zoom <= 1} onClick={() => setZoom(z => Math.max(1, z - 0.25))}><ZoomOut className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="sm" aria-label="PDF auf Breite einpassen" onClick={() => { setZoom(1); viewportRef.current?.scrollTo?.(0, 0); }}>{zoom === 1 ? 'Einpassen' : `${Math.round(zoom * 100)} %`}</Button>
            <Button type="button" variant="outline" size="icon" aria-label="PDF vergrößern" disabled={zoom >= 3} onClick={() => setZoom(z => Math.min(3, z + 0.25))}><ZoomIn className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <div className={`relative ${compactLandscape ? "aspect-[210/148] max-h-[62dvh] flex-none" : "flex-1 min-h-0"}`}>
      <div ref={viewportRef} className={`h-full rounded-xl border border-[#E5E5EA] bg-[#F5F5F7] overflow-auto py-3 ${sideNavigation && !useNativeFallback ? "px-2 sm:px-14" : "px-2 sm:px-3"}`}>

        {error ? (
          <p className="text-sm text-[#FF3B30]">{error}</p>
        ) : useNativeFallback ? (
          <iframe
            title={`${title} Fallback`}
            src={`${fileUrl || ""}#toolbar=0&navpanes=0&scrollbar=0`}
            className="h-full min-h-[520px] w-full rounded-md border-0 bg-white"
          />
        ) : loading && !docRef.current ? (
          <p className="text-sm text-[#86868B]">PDF wird geladen...</p>
        ) : (
          <div className={`flex min-h-full items-start ${mobile && zoom > 1 ? "w-max min-w-full justify-start" : "w-full justify-center"}`}>
            <canvas ref={canvasRef} className={`${mobile && zoom > 1 ? "max-w-none shrink-0" : "max-w-full"} h-auto bg-white shadow-sm rounded-md`} />
          </div>
        )}
      </div>
      {sideNavigation && !mobile && !useNativeFallback && !error && <>
        <Button type="button" variant="outline" size="icon" aria-label="Vorherige PDF-Seite" title="Vorherige Seite"
          className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-md disabled:opacity-35"
          disabled={loading || page <= 1} onClick={()=>{setPage(p=>Math.max(1,p-1));if(viewportRef.current)viewportRef.current.scrollTop=0;}}>
          <ChevronLeft className="h-5 w-5"/>
        </Button>
        <Button type="button" variant="outline" size="icon" aria-label="Nächste PDF-Seite" title="Nächste Seite"
          className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-md disabled:opacity-35"
          disabled={loading || !pageCount || page >= pageCount} onClick={()=>{setPage(p=>Math.min(pageCount,p+1));if(viewportRef.current)viewportRef.current.scrollTop=0;}}>
          <ChevronRight className="h-5 w-5"/>
        </Button>
      </>}
      </div>
    </div>
  );
}
